import { createContext, useContext, useId, useMemo } from 'react'
import type {
  FormEvent,
  JSX,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  Ref,
} from 'react'

import {
  ArrowUpIcon,
  IconButton,
  Textarea,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import styles from './NoteForm.module.css'

type FormContextValue = {
  inputId: string
  errorId: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

const FormContext = createContext<FormContextValue | null>(null)

function handleInputKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
    event.preventDefault()
    event.currentTarget.form?.requestSubmit()
  }
}

function useFormContext(): FormContextValue {
  const context = useContext(FormContext)
  if (!context) throw new Error('NoteForm parts need a NoteForm parent')
  return context
}

export type NoteFormProps = {
  value: string
  onChange: (value: string) => void
  onSubmit: (value: string) => void
  pending?: boolean
  disabled?: boolean
  children: ReactNode
}

function Root({
  value,
  onChange,
  onSubmit,
  pending = false,
  disabled = false,
  children,
}: NoteFormProps): JSX.Element {
  const inputId = useId()
  const errorId = useId()
  const submitDisabled =
    pending || disabled || value.length > 500 || !value.trim()
  const contextValue = useMemo(
    () => ({ inputId, errorId, value, onChange, disabled: submitDisabled }),
    [inputId, errorId, value, onChange, submitDisabled],
  )

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (!submitDisabled) onSubmit(value)
  }

  return (
    <FormContext.Provider value={contextValue}>
      <form className={styles.form} onSubmit={submit} autoComplete="off">
        {children}
      </form>
    </FormContext.Provider>
  )
}

function Input({
  label,
  placeholder,
  inputRef,
}: {
  label: string
  placeholder?: string
  inputRef?: Ref<HTMLTextAreaElement>
}): JSX.Element {
  const { inputId, errorId, value, onChange } = useFormContext()

  return (
    <>
      <VisuallyHidden as="label" htmlFor={inputId}>
        {label}
      </VisuallyHidden>
      <Textarea
        ref={inputRef}
        id={inputId}
        className={styles.input}
        name="noteText"
        rows={2}
        placeholder={placeholder}
        maxLength={500}
        value={value}
        aria-describedby={errorId}
        aria-invalid={value.length > 500}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleInputKeyDown}
      />
    </>
  )
}

function Actions({ children }: { children: ReactNode }): JSX.Element {
  return <div className={styles.actions}>{children}</div>
}

function Submit({
  label,
  children,
}: {
  label: string
  children?: ReactElement
}): JSX.Element {
  const { disabled } = useFormContext()
  return (
    <IconButton
      label={label}
      variant="primary"
      type="submit"
      disabled={disabled}
    >
      {children ?? <ArrowUpIcon />}
    </IconButton>
  )
}

function FormError({ children }: { children: ReactNode }): JSX.Element {
  const { errorId } = useFormContext()
  return (
    <div id={errorId} className={styles.error} role="alert">
      {children}
    </div>
  )
}

export const NoteForm = Object.assign(Root, {
  Input,
  Actions,
  Submit,
  Error: FormError,
})
